import React from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Feature, FeatureCollection, Geometry } from "geojson";

import { baseDrawControlModes } from "fond/draw/helper";
import { onAfterDragAerial } from "fond/draw/modes/planner/aerial/onAfterDragAerial";
import { onTrashAerial } from "fond/draw/modes/planner/aerial/onTrashAerial";
import { makeValidateAerialPole, makeValidateLeadIn, makeValidateUgPath, makeValidateUgPoint } from "fond/draw/validation";
import {
  DeleteButton,
  LineStringButton,
  PointButton,
  PolygonButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  TopologicalButton,
  UndoButton,
} from "fond/map/Toolbar";
import { featureCollection } from "fond/turf";
import { EditingLayer, LayerId, LayerIds } from "fond/types";

import { compositeDrawLineString, compositeDrawPoint } from "./modes";

/** Geometry types with a draw tool, in toolbar order. */
const DRAW_TOOL_GEOMETRY_TYPES = ["Point", "LineString", "Polygon"] as const satisfies ReadonlyArray<Geometry["type"]>;

const DRAW_TOOL_BUTTONS: Record<(typeof DRAW_TOOL_GEOMETRY_TYPES)[number], React.ReactElement> = {
  Point: <PointButton key="drawPoint" />,
  LineString: <LineStringButton key="drawLine" />,
  Polygon: <PolygonButton key="drawPolygon" />,
};

/** How to draw features of each geometry type. */
export const DRAW_HINTS: Partial<Record<Geometry["type"], string>> = {
  Point: "Click to place points; drag to move",
  LineString: "Click to add vertices; double-click to finish",
  Polygon: "Click to add corners; double-click to finish",
};

/** Return the editing toolbar with draw tools for the given geometry types. */
export const makeEditToolbarConfig = (geometryTypes: ReadonlyArray<Geometry["type"]>): ToolbarConfig => [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
    ],
  },
  {
    id: "drawTools",
    type: "mode",
    items: DRAW_TOOL_GEOMETRY_TYPES.filter((type) => geometryTypes.includes(type)).map((type) => DRAW_TOOL_BUTTONS[type]),
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />, <TopologicalButton key="topological" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const LEAD_IN_VALIDATION_LAYERS = {
  demandLayerId: LayerIds.inAddress,
  ugPointsLayerId: LayerIds.inStreetPoints,
  aerialPoleLayerId: LayerIds.inPole,
};

const UG_POINT_VALIDATION_LAYERS = {
  ugPathLayerId: LayerIds.inStreet,
  leadinsLayerId: LayerIds.inLeadIns,
};

const UG_PATH_VALIDATION_LAYERS = {
  ugPointsLayerId: LayerIds.inStreetPoints,
  aerialPoleLayerId: LayerIds.inPole,
};

const AERIAL_POLE_VALIDATION_LAYERS = {
  spanLayerId: LayerIds.inSpan,
};

/** Other layers each planner layer's validator reads. */
const VALIDATION_DEPENDENCIES: Partial<Record<LayerId, LayerId[]>> = {
  [LayerIds.inLeadIns]: Object.values(LEAD_IN_VALIDATION_LAYERS),
  [LayerIds.inStreetPoints]: Object.values(UG_POINT_VALIDATION_LAYERS),
  [LayerIds.inStreet]: Object.values(UG_PATH_VALIDATION_LAYERS),
  [LayerIds.inPole]: Object.values(AERIAL_POLE_VALIDATION_LAYERS),
};

const AERIAL_LAYER_IDS: LayerId[] = [LayerIds.inPole, LayerIds.inSpan];

/** Editing configuration of each editable planner layer. */
const EDITING_LAYERS: Partial<Record<LayerId, EditingLayer>> = {
  [LayerIds.inAddress]: {
    ID: LayerIds.inAddress,
    GeometryType: "Point",
    getFeatureProperties: () => ({ layerId: LayerIds.inAddress }),
  },
  [LayerIds.inParcel]: {
    ID: LayerIds.inParcel,
    GeometryType: "Polygon",
    getFeatureProperties: () => ({ layerId: LayerIds.inParcel }),
  },
  [LayerIds.inBaseDataStreet]: {
    ID: LayerIds.inBaseDataStreet,
    GeometryType: "LineString",
    getFeatureProperties: () => ({ layerId: LayerIds.inBaseDataStreet }),
  },
  [LayerIds.inLeadIns]: {
    ID: LayerIds.inLeadIns,
    GeometryType: "LineString",
    validate: makeValidateLeadIn(LEAD_IN_VALIDATION_LAYERS),
    options: {
      // Leadins are always a single segment between a demand point and the network.
      midPoints: false,
      maxVertices: 2,
    },
    getFeatureProperties: () => ({ layerId: LayerIds.inLeadIns }),
  },
  [LayerIds.inStreetPoints]: {
    ID: LayerIds.inStreetPoints,
    GeometryType: "Point",
    validate: makeValidateUgPoint(UG_POINT_VALIDATION_LAYERS),
    getFeatureProperties: () => ({ layerId: LayerIds.inStreetPoints }),
  },
  [LayerIds.inStreet]: {
    ID: LayerIds.inStreet,
    GeometryType: "LineString",
    validate: makeValidateUgPath(UG_PATH_VALIDATION_LAYERS),
    getFeatureProperties: () => ({ layerId: LayerIds.inStreet }),
  },
  [LayerIds.inSpan]: {
    ID: LayerIds.inSpan,
    GeometryType: "LineString",
    options: { midPoints: false },
    // The span layer enforced topological editing with the pole layer.
    compulsoryTopologicalLayerIds: AERIAL_LAYER_IDS,
    // The span layer has custom onAfterDrag and onTrash handlers to manage pole merging, span splitting, and snapped status.
    onAfterDrag: onAfterDragAerial,
    onTrash: onTrashAerial,
  },
  [LayerIds.inPole]: {
    ID: LayerIds.inPole,
    GeometryType: "Point",
    validate: makeValidateAerialPole(AERIAL_POLE_VALIDATION_LAYERS),
    // The pole layer enforced topological editing with the span layer.
    compulsoryTopologicalLayerIds: AERIAL_LAYER_IDS,
    // The pole layer has custom onAfterDrag and onTrash handlers to manage pole merging, span splitting, and snapped status.
    onAfterDrag: onAfterDragAerial,
    onTrash: onTrashAerial,
  },
  [LayerIds.inExchange]: {
    ID: LayerIds.inExchange,
    GeometryType: "Point",
    getFeatureProperties: () => ({ layerId: LayerIds.inExchange }),
  },
};

/** Return the editing configuration of a planner layer. */
export const makeEditingLayer = (layerId: LayerId): EditingLayer => {
  const layer = EDITING_LAYERS[layerId];
  if (!layer) throw new Error(`Layer ${layerId} is not editable`);
  return { ...layer };
};

/**
 * Return the editing layers for editing the given planner layers.
 *
 * Layers their validators read are included as hidden layers, available for snapping and validation but not edited or saved.
 */
export const makeEditingLayers = (layerIds: readonly LayerId[]): EditingLayer[] => {
  const supportLayerIds = [...new Set(layerIds.flatMap((layerId) => VALIDATION_DEPENDENCIES[layerId] ?? []))].filter(
    (layerId) => !layerIds.includes(layerId)
  );
  return [
    ...layerIds.map(makeEditingLayer),
    ...supportLayerIds.map((layerId): EditingLayer => ({ ID: layerId, GeometryType: makeEditingLayer(layerId).GeometryType, Hide: true })),
  ];
};

/** Draw modes for editing planner layers. */
export const EDIT_DRAW_MODES: MapboxDrawOptions["modes"] = {
  ...baseDrawControlModes,
  draw_point: compositeDrawPoint,
  draw_line_string: compositeDrawLineString,
};

/** Return the draw options for editing planner layers. */
export const makeEditDrawOptions = ({
  layers,
  editingLayerId,
  editingLayerIds,
}: {
  layers: EditingLayer[];
  editingLayerId: LayerId;
  /** Layers whose features can be edited; all layers when omitted. */
  editingLayerIds?: LayerId[];
}): MapboxDrawOptions => ({
  snap: true,
  topologicalEditing: true,
  snapOptions: {
    snapVertexPriorityDistance: 0.000025,
  },
  modes: EDIT_DRAW_MODES,
  editOptions: {
    allLayers: layers,
    editingLayerId,
    ...(editingLayerIds && { editingLayerIds }),
  },
});

/** Return the features of the given layers, or undefined when there are none. */
export const getEditFeatures = (
  layers: Partial<Record<string, FeatureCollection>> | undefined,
  layerIds: readonly string[]
): FeatureCollection | undefined => {
  const features: Feature[] = layerIds.flatMap((layerId) => layers?.[layerId]?.features ?? []);
  return features.length > 0 ? featureCollection(features) : undefined;
};
